import React from 'react';
import { Global, css } from '@emotion/react';
import { useTheme } from '@mui/material/styles';

// Load all Vellum fonts (Google Fonts + local brand + local reader).
// @font-face declarations don't download files until the font-family is
// actually used in the document, so including reader fonts here is safe.
import '../../styles/fonts/index.css';

/**
 * Codex global styles — replaces global.css.
 * The Codex theme already emits all CSS variables via MuiCssBaseline
 * (--color-*, --spacing-*, --radius-*, --font-*, --duration-*).
 * This component adds only app-level resets and keyframe animations.
 */
export default function GlobalStyles() {
  const theme = useTheme();

  return (
    <Global
      styles={css`
        /* Smooth anchor scrolling + reserve scrollbar gutter so layout never
           shifts when a scrollbar appears/disappears. */
        html {
          scroll-behavior: smooth;
          scrollbar-gutter: stable;
        }

        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(128, 128, 128, 0.35);
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(128, 128, 128, 0.55);
        }

        html,
        body {
          margin: 0;
          padding: 0;
          font-size: 16px;
          overflow: hidden;
        }

        * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        img {
          max-width: 100%;
          display: block;
        }
        button {
          font-family: inherit;
        }
        a {
          color: inherit;
        }

        /* Vellum app shell */
        #root {
          min-height: 100vh;
          background: ${theme.palette.background.body ?? theme.palette.background.default};
        }

        /* ProseMirror / TipTap editor */
        .ProseMirror {
          outline: none;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: ${theme.palette.text.disabled};
          pointer-events: none;
          height: 0;
        }

        /* Keyframes */
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes vl-shimmer {
          0% {
            background-position: 0% center;
          }
          100% {
            background-position: 200% center;
          }
        }
        @keyframes vl-card-float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-12px);
          }
        }
        @keyframes vl-orb-1 {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(30px, 20px) scale(1.1);
          }
        }
        @keyframes vl-orb-2 {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(-20px, 30px) scale(0.9);
          }
        }
        @keyframes vl-orb-3 {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
          }
          50% {
            transform: translate(20px, -20px) scale(1.05);
          }
        }
        @keyframes vl-pulse-dot {
          0%,
          100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(0.8);
          }
        }
      `}
    />
  );
}
